import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaComment, FaCheck, FaUpload } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';

// Simple fetch wrapper
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
};

// LoadPermitsPanel — editable permits + escorts checklist for a load (admin/subbie/client visible)
export default function LoadPermitsPanel({ loadId }) {
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPermit, setNewPermit] = useState({ permit_type: 'abnormal', jurisdiction: '', title: '', description: '' });
  const [commentText, setCommentText] = useState({}); // { [permitId]: string }

  const fetchPermits = async () => {
    if (!loadId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/load-permits?load_id=${loadId}`);
      if (data.success) setPermits(data.permits || []);
    } catch (err) {
      console.error('Error fetching permits:', err);
      toast.error('Failed to load permits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPermits(); }, [loadId]);

  const addPermit = async () => {
    if (!loadId) return;
    try {
      await apiFetch('/load-permits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ load_id: loadId, ...newPermit })
      });
      setNewPermit({ permit_type: 'abnormal', jurisdiction: '', title: '', description: '' });
      fetchPermits();
    } catch (err) {
      console.error('Error adding permit:', err);
      toast.error('Failed to add permit');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await apiFetch(`/load-permits/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchPermits();
    } catch (err) {
      console.error('Error updating permit:', err);
      toast.error('Failed to update status');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // permit id to delete

  const deletePermit = (id) => {
    setDeleteTarget(id);
  };

  const performDeletePermit = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/load-permits/${deleteTarget}`, { method: 'DELETE' });
      fetchPermits();
    } catch (err) {
      console.error('Error deleting permit:', err);
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  const addComment = async (permitId) => {
    const text = (commentText[permitId] || '').trim();
    if (!text) return;
    try {
      await apiFetch('/load-permits/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permit_id: permitId, comment: text })
      });
      setCommentText(prev => ({ ...prev, [permitId]: '' }));
      fetchPermits();
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    }
  };

  const permitTypeOptions = [
    'abnormal', 'route_survey', 'import', 'export', 'rigging', 'escort', 'customs', 'other'
  ];

  if (loading) return <div className="text-sm text-gray-500 py-4">Loading permits...</div>;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-navy">📄 Permits & Escorts</h3>
        <span className="text-xs text-gray-500">{permits.length} item(s)</span>
      </div>

      {permits.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
          No permits or escorts added for this load.
        </p>
      )}

      <div className="space-y-2">
        {permits.map(p => (
          <div key={p.id} className="border border-gray-200 rounded-lg p-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-navy text-sm">{p.title || p.permit_type}</p>
                <p className="text-xs text-gray-500">
                  {p.permit_type}{p.jurisdiction ? ` · ${p.jurisdiction}` : ''}
                </p>
                {p.description && <p className="text-xs text-gray-600 mt-1">{p.description}</p>}
                {p.file_url && (
                  <a href={p.file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline inline-flex items-center gap-1 mt-1">
                    <FaUpload size={10} /> View upload
                  </a>
                )}
              </div>
              <div className="flex items-center gap-1">
                <select
                  value={p.status}
                  onChange={(e) => updateStatus(p.id, e.target.value)}
                  className="text-xs border border-gray-200 rounded px-1 py-0.5"
                >
                  <option value="required">Required</option>
                  <option value="uploaded">Uploaded</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="not_required">Not required</option>
                </select>
                <button onClick={() => deletePermit(p.id)} className="text-red-400 hover:text-red-600">
                  <FaTrash size={12} />
                </button>
              </div>
            </div>

            {/* Append-only comments */}
            <div className="mt-2 flex gap-2">
              <input
                value={commentText[p.id] || ''}
                onChange={(e) => setCommentText(prev => ({ ...prev, [p.id]: e.target.value }))}
                placeholder="Add note/comment (history kept)..."
                className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
              />
              <button
                onClick={() => addComment(p.id)}
                className="px-2 py-1 text-xs bg-green text-white rounded hover:bg-green flex items-center gap-1"
              >
                <FaComment size={10} /> Add
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add entry */}
      <div className="mt-3 border border-dashed border-gray-200 rounded-lg p-3">
        <p className="text-xs font-medium text-gray-600 mb-2">Add permit / escort / item</p>
        <div className="flex flex-wrap gap-2">
          <select
            value={newPermit.permit_type}
            onChange={(e) => setNewPermit({ ...newPermit, permit_type: e.target.value })}
            className="px-2 py-1 text-sm border border-gray-200 rounded"
          >
            {permitTypeOptions.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
          <input
            value={newPermit.title}
            onChange={(e) => setNewPermit({ ...newPermit, title: e.target.value })}
            placeholder="Title (e.g. Abnormal — Gauteng)"
            className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
          />
          <input
            value={newPermit.jurisdiction}
            onChange={(e) => setNewPermit({ ...newPermit, jurisdiction: e.target.value })}
            placeholder="Province/country"
            className="w-32 px-2 py-1 text-sm border border-gray-200 rounded"
          />
          <button onClick={addPermit} className="px-3 py-1 text-sm bg-green text-white rounded hover:bg-green flex items-center gap-1">
            <FaPlus size={12} /> Add
          </button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remove Entry"
        message="Remove this permit/checklist entry?"
        confirmLabel="Remove"
        danger
        onConfirm={performDeletePermit}
      />
    </div>
  );
}